// rc123 — Dialogo "Tarjeta de pago": imagen descargable/compartible con los
// datos bancarios del cliente (CLABE SPEI + beneficiario segun su empresa).
// La CLABE llega YA CACHEADA desde TokuClabeCard (toku_entity_map.clabe) y la
// razon social sale de /api/v1/empresas — este dialogo NO toca Toku jamas.
// En movil el boton principal usa Web Share (archivo) -> "Guardar imagen" a la
// galeria del iPhone; en desktop descarga el PNG. Espejo de WifiCardDialog.
import { useEffect, useRef, useState } from "react";
import { AlertCircle, Download, Share2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { renderPagoCard } from "@/lib/pagoCard";

type EmpresaOpt = {
  id: number;
  nombre: string;
  razon_social?: string | null;
};

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  clabe: string;
  empresaId?: number;
  clienteNombre?: string | null;
  clienteCodigo?: string | null;
};

export function PagoCardDialog({
  open, onOpenChange, clabe, empresaId, clienteNombre, clienteCodigo,
}: Props) {
  const [preview, setPreview] = useState("");
  const [genError, setGenError] = useState<string | null>(null);
  const blobRef = useRef<Blob | null>(null);

  const { data: empresas = [], isFetching } = useQuery<EmpresaOpt[]>({
    queryKey: ["empresas", "for-pago-card"],
    queryFn: () => apiCall<EmpresaOpt[]>("get", "/api/v1/empresas"),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const emp = empresas.find((e) => e.id === (empresaId ?? 1));
  // rc129 — si la empresa no carga, cae al nombre comercial del ISP (no a "BYTECOM" fijo).
  const { data: brand } = useBranding();
  const beneficiario = (emp?.razon_social || "").trim() || emp?.nombre || brand?.nombre || "";
  const empresaNombre = emp?.nombre || brand?.nombre || "";

  useEffect(() => {
    let alive = true;
    setPreview("");
    setGenError(null);
    blobRef.current = null;
    if (!open || !clabe || isFetching) return;
    renderPagoCard({
      clabe,
      beneficiario,
      empresaNombre,
      clienteNombre: clienteNombre ?? "",
      clienteCodigo: clienteCodigo ?? "",
    })
      .then((b) => {
        if (!alive) return;
        blobRef.current = b;
        setPreview(URL.createObjectURL(b));
      })
      .catch(() => alive && setGenError("No se pudo generar la imagen."));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clabe, isFetching, beneficiario, empresaNombre]);

  const fileName = `pago-${(clienteCodigo || clabe).replace(/[^\w.-]+/g, "_")}.png`;

  function download() {
    if (!blobRef.current) return;
    const url = URL.createObjectURL(blobRef.current);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function shareOrDownload() {
    if (!blobRef.current) return;
    // Web Share nivel 2 (archivos): en iPhone abre la hoja nativa con
    // "Guardar imagen" -> galeria. Si no esta disponible, descarga normal.
    const file = new File([blobRef.current], fileName, { type: "image/png" });
    const nav = navigator as Navigator & {
      canShare?: (d: { files: File[] }) => boolean;
      share?: (d: { files: File[] }) => Promise<void>;
    };
    if (nav.canShare?.({ files: [file] }) && nav.share) {
      try {
        await nav.share({ files: [file] });
        return;
      } catch {
        return; // usuario cancelo la hoja
      }
    }
    download();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="sm">
      <DialogHeader>
        <DialogTitle>Tarjeta de pago del cliente</DialogTitle>
        <DialogDescription>
          CLABE SPEI a nombre de {beneficiario} — lista para compartir o imprimir.
        </DialogDescription>
      </DialogHeader>
      <DialogContent>
        {!clabe ? (
          <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>Este cliente no tiene CLABE sincronizada todavia.</span>
          </div>
        ) : genError ? (
          <p className="py-6 text-center text-xs text-destructive">{genError}</p>
        ) : preview ? (
          <img
            src={preview}
            alt="Tarjeta de pago"
            className="mx-auto max-h-[52vh] rounded-lg border"
          />
        ) : (
          <p className="py-8 text-center text-xs text-muted-foreground">
            Generando tarjeta…
          </p>
        )}
      </DialogContent>
      <DialogFooter>
        <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
          Cerrar
        </Button>
        <Button
          type="button" variant="outline" size="sm"
          onClick={download}
          disabled={!preview}
        >
          <Download size={13} className="mr-1.5" />
          Descargar
        </Button>
        <Button
          type="button" size="sm"
          onClick={shareOrDownload}
          disabled={!preview}
        >
          <Share2 size={13} className="mr-1.5" />
          Compartir / Guardar
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
